import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/Login";
import { Upload, Download, CheckCircle2, AlertTriangle, FileText, Tag as TagIcon, Trash2, Plus } from "lucide-react";

/* -------- Importer -------- */
export function ContentImporter() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [strategy, setStrategy] = useState("skip");
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const fileRef = useRef(null);

  const loadJobs = () => api.get("/admin/import/jobs").then(({ data }) => setJobs(data.jobs || []));
  useEffect(() => { loadJobs(); }, []);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/import/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "clubinho-import-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doValidate = async () => {
    if (!file) return toast.error("Selecione um arquivo primeiro.");
    setBusy(true); setResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/import/validate", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); setPreview(null); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("strategy", strategy);
    fd.append("publish_immediately", publishImmediately ? "true" : "false");
    try {
      const { data } = await api.post("/admin/import/confirm", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data.job);
      setPreview(null); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success(`Importação concluída: ${data.job.new} novos, ${data.job.updated} atualizados`);
      loadJobs();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadErrors = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/import/jobs/${jobId}/errors.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `job-${jobId}-errors.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-importer" className="space-y-6">
      <div className="card-soft p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-extrabold text-[#243B53]">Importar Conteúdos</h2>
            <p className="text-sm text-[#243B53]/60">Envie um arquivo CSV ou JSON com até 2000 registros.</p>
          </div>
          <button data-testid="import-download-template" onClick={downloadTemplate} className="btn-outline inline-flex items-center gap-2 text-sm">
            <Download size={16}/> Baixar modelo CSV
          </button>
        </div>

        <div className="mt-4 grid gap-4">
          <div>
            <label className="text-sm font-bold text-[#243B53] block mb-1">1. Arquivo (.csv ou .json)</label>
            <input ref={fileRef} type="file" accept=".csv,.json" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
              data-testid="import-file-input" className="text-sm" />
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <button data-testid="import-validate-btn" disabled={!file || busy} onClick={doValidate}
              className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50">
              <CheckCircle2 size={16}/> {busy && !preview ? "Validando…" : "2. Validar (dry-run)"}
            </button>
            <select data-testid="import-strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)}
              className="rounded-full border border-[#243B53]/15 bg-white px-3 py-1.5 text-sm">
              <option value="skip">Estratégia: SKIP (ignora existentes)</option>
              <option value="update">Estratégia: UPDATE (atualiza campos presentes)</option>
            </select>
            <label className="text-sm inline-flex items-center gap-2">
              <input type="checkbox" checked={publishImmediately} onChange={(e) => setPublishImmediately(e.target.checked)}
                data-testid="import-publish-toggle"/>
              Publicar imediatamente (novos)
            </label>
          </div>
        </div>
      </div>

      {preview && (
        <div className="card-soft p-5" data-testid="import-preview">
          <h3 className="text-lg font-extrabold text-[#243B53]">Preview</h3>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Total" value={preview.total} />
            <Stat label="Válidos" value={preview.valid} accent="green" />
            <Stat label="Inválidos" value={preview.invalid} accent={preview.invalid ? "red" : ""} />
            <Stat label="Novos" value={preview.new} />
            <Stat label="Duplicados" value={preview.duplicates} />
          </div>
          {preview.errors?.length > 0 && (
            <details className="mt-4" data-testid="import-preview-errors">
              <summary className="text-sm font-bold text-red-600 cursor-pointer inline-flex items-center gap-1">
                <AlertTriangle size={14}/> Ver {preview.errors.length} erro(s)
              </summary>
              <div className="mt-2 max-h-64 overflow-y-auto text-xs">
                <table className="w-full">
                  <thead className="bg-[#FFF9ED]"><tr><th className="p-2 text-left">Linha</th><th className="p-2 text-left">Campo</th><th className="p-2 text-left">Motivo</th></tr></thead>
                  <tbody>
                    {preview.errors.map((e, i) => (
                      <tr key={i} className="border-t"><td className="p-2">{e.row}</td><td className="p-2 font-mono">{e.field}</td><td className="p-2">{e.reason}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          <div className="mt-4 flex gap-2 flex-wrap">
            <button data-testid="import-confirm-btn" disabled={busy || preview.valid === 0} onClick={doConfirm}
              className="btn-primary text-sm disabled:opacity-50">
              3. Confirmar importação ({preview.valid} válidos)
            </button>
            <button onClick={() => setPreview(null)} className="btn-outline text-sm">Cancelar</button>
          </div>
        </div>
      )}

      {result && (
        <div className="card-soft p-5" data-testid="import-result">
          <h3 className="text-lg font-extrabold text-[#243B53]">Resultado</h3>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Novos" value={result.new} accent="green" />
            <Stat label="Atualizados" value={result.updated} />
            <Stat label="Ignorados" value={result.skipped} />
            <Stat label="Falhas" value={result.failed} accent={result.failed ? "red" : ""} />
          </div>
          {result.errors?.length > 0 && (
            <button data-testid="import-result-download-errors" onClick={() => downloadErrors(result.job_id)} className="btn-outline text-sm mt-4 inline-flex items-center gap-2">
              <Download size={14}/> Baixar relatório de erros
            </button>
          )}
        </div>
      )}

      {/* Recent jobs */}
      <div className="card-soft p-5">
        <h3 className="text-lg font-extrabold text-[#243B53] mb-3">Importações recentes</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FFF9ED] text-left"><tr>
              <th className="p-2">Arquivo</th><th className="p-2">Estratégia</th><th className="p-2">Novos</th><th className="p-2">Atualizados</th><th className="p-2">Falhas</th><th className="p-2">Status</th><th className="p-2">Quando</th><th className="p-2"></th>
            </tr></thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_id} className="border-t" data-testid={`import-job-${j.job_id}`}>
                  <td className="p-2 font-bold flex items-center gap-1"><FileText size={12}/> {j.filename}</td>
                  <td className="p-2">{j.strategy}</td>
                  <td className="p-2">{j.new}</td>
                  <td className="p-2">{j.updated}</td>
                  <td className="p-2">{j.failed}</td>
                  <td className="p-2"><span className="chip">{j.status}</span></td>
                  <td className="p-2 text-xs">{j.started_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2">
                    {j.errors?.length > 0 && (
                      <button onClick={() => downloadErrors(j.job_id)} className="btn-outline text-xs">Erros</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "" }) {
  const color = accent === "green" ? "text-emerald-600" : accent === "red" ? "text-red-600" : "text-[#243B53]";
  return (
    <div className="rounded-2xl bg-[#FFF9ED] p-3 text-center">
      <div className={`text-2xl font-extrabold ${color}`}>{value ?? 0}</div>
      <div className="text-[10px] font-bold text-[#243B53]/60 uppercase">{label}</div>
    </div>
  );
}

/* -------- Tags Manager -------- */
export function TagsManager() {
  const [tags, setTags] = useState([]);
  const [label, setLabel] = useState("");
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/tags", { params: q ? { q } : {} }).then(({ data }) => setTags(data.tags || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  const add = async () => {
    if (!label.trim()) return;
    try { await api.post("/admin/tags", { label: label.trim() }); setLabel(""); toast.success("Tag criada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (key) => {
    if (!window.confirm("Arquivar tag?")) return;
    await api.delete(`/admin/tags/${key}`); toast.success("Arquivada"); load();
  };
  return (
    <div data-testid="admin-tags-manager" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-extrabold text-[#243B53] inline-flex items-center gap-2"><TagIcon size={20}/> Tags</h2>
          <p className="text-sm text-[#243B53]/60">{tags.length} tag(s) ativas</p>
        </div>
        <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar tag" data-testid="admin-tags-search"
          className="rounded-full border border-[#243B53]/15 bg-white px-4 py-2 text-sm w-60"/>
      </div>
      <div className="card-soft p-4 flex gap-2 flex-wrap items-center">
        <input value={label} onChange={(e)=>setLabel(e.target.value)} placeholder="Nome da tag" data-testid="admin-tag-new-input"
          className="rounded-full border border-[#243B53]/15 bg-white px-4 py-2 text-sm flex-1 min-w-[200px]"/>
        <button data-testid="admin-tag-add-btn" onClick={add} className="btn-primary text-sm inline-flex items-center gap-2">
          <Plus size={14}/> Adicionar
        </button>
      </div>
      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF9ED] text-left"><tr>
            <th className="p-3">Label</th><th className="p-3">Chave</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {tags.map((t) => (
              <tr key={t.key} className="border-t" data-testid={`admin-tag-row-${t.key}`}>
                <td className="p-3 font-bold">{t.label}</td>
                <td className="p-3 text-xs font-mono">{t.key}</td>
                <td className="p-3">
                  <button onClick={()=>remove(t.key)} data-testid={`admin-tag-archive-${t.key}`}
                    className="btn-outline text-xs inline-flex items-center gap-1"><Trash2 size={12}/> Arquivar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
